import { ChangeDetectionStrategy, Component, computed, effect, Inject, inject, InjectionToken, Injector, input, Input, OnInit, output, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, startWith } from 'rxjs';
import { valuePairToValue } from '@softpak/components/spx-helpers';
import { ContainerTypeEnum } from 'src/app/shared/models/shared/shared-containertype.enum';
import { SharedPageTypeEnum } from '../../navigation/page-type.enum';
import { sharedContainerSearchActions } from 'src/app/store/shared-container-search';
import { sharedContainerSingleActions } from 'src/app/store/shared-container-single';
import { sharedDepotInfoReducer } from 'src/app/store/shared-depot-info';
import { sharedValidator } from '../../_config/validators/validators';
import { spValRequired } from '../../validators';
import { ctrlCassetteCode, ctrlContainerNumber, ctrlContainerPrefix, ctrlContainerSearch, ctrlContainerType, ctrlSequenceNumber } from '../../_config/ctrls/ctrls-constants';
import { toSignal } from '@angular/core/rxjs-interop';
import { SharedSearchContainerComponent } from './shared-search-container.component';
import { SharedSearchCasetteComponent } from './shared-search-casette.component';
import { SharedSearchTrailerComponent } from './shared-search-trailer.component';
import { SharedSearchMiscellaneousComponent } from './shared-search-miscellaneous.component';
import { SharedSearchTypeComponent, SharedSearchTypeChangeEvent } from './shared-search-type.component';
import { SharedSearchChildContextI } from './shared-search-child-context.interface';

export const SHARED_PAGE_TYPE = new InjectionToken<SharedPageTypeEnum>('SHARED_PAGE_TYPE');

@Component({
  selector: 'shared-search',
  templateUrl: './shared-search.component.html',
  imports: [
    SharedSearchTypeComponent,
    SharedSearchContainerComponent,
    SharedSearchCasetteComponent,
    SharedSearchTrailerComponent,
    SharedSearchMiscellaneousComponent,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedSearchComponent implements OnInit {
  protected readonly injector = inject(Injector);
  private readonly appStore = inject(Store);
  private isResetting = false;
  private lastLoadedSeqNr: string | number | null = null;

  @Input() formGroup: FormGroup;

  private readonly unitTypeFormView = viewChild(SharedSearchTypeComponent);
  private readonly searchContainerFormView = viewChild(SharedSearchContainerComponent);
  private readonly searchCasetteFormView = viewChild(SharedSearchCasetteComponent);
  private readonly trailerFieldsFormView = viewChild(SharedSearchTrailerComponent);
  private readonly miscellaneousFieldsFormView = viewChild(SharedSearchMiscellaneousComponent);

  buttonClick = output<any>();
  containerClear = output();

  inQuayjobId = input<number>(0);
  recordType = input<number>(30);
  containerType = signal<ContainerTypeEnum>(ContainerTypeEnum.container);
  searchParameters = input<{}>({});
  showSearch = input<boolean>(false);
  pageTypeSignal = input<SharedPageTypeEnum>(undefined);
  resolvedPageType = computed<SharedPageTypeEnum>(() => this.pageTypeSignal() ?? this.pageType);
  childContext = computed<SharedSearchChildContextI>(() => ({
    pageType: this.resolvedPageType(),
    shouldShowRoRo: this.shouldShowRoRo(),
    showSearch: this.showSearch(),
    searchParameters: this.searchParameters(),
    recordType: this.recordType(),
    inQuayjobId: this.inQuayjobId(),
    isMiscellaneous: this.isMiscellaneous(),
  }));
  isTrailer = computed<boolean>(() => this.containerType() === ContainerTypeEnum.trailer);
  isMiscellaneous = computed<boolean>(() => this.containerType() === ContainerTypeEnum.miscellaneous);
  isCasette = computed<boolean>(() => this.containerType() === ContainerTypeEnum.cassette);
  showRoroForComponent = input<boolean>(false);
  showRoRo = this.appStore.selectSignal(sharedDepotInfoReducer.default.selectShowRoRo);
  shouldShowRoRo = computed(() => this.showRoroForComponent() && this.showRoRo());

  private readonly ctrlContainerPrefix = computed(() => this.formGroup.get(ctrlContainerPrefix) as FormControl);
  private readonly ctrlContainerNumber = computed(() => this.formGroup.get(ctrlContainerNumber) as FormControl);
  private readonly ctrlCassetteCode = computed(() => this.formGroup.get(ctrlCassetteCode) as FormControl);
  private readonly ctrlContainerSearch = computed(() => this.formGroup.get(ctrlContainerSearch) as FormControl);
  private readonly ctrlSeqNr = computed(() => this.formGroup.get(ctrlSequenceNumber) as FormControl);
  private readonly ctrlContainerType = computed(() => this.formGroup.get(ctrlContainerType) as FormControl);

  constructor(
    @Inject(SHARED_PAGE_TYPE) public readonly pageType: SharedPageTypeEnum
  ) { }

  ngOnInit(): void {
    this.ensureContainerTypeControl();
    this.listenToSeqNr();
  }

  ionViewDidEnter(): void {
    // kept for compatibility with callers; no-op now
  }

  ionViewWillLeave(): void {
    // kept for compatibility with callers; no-op now
  }

  createForm(): FormGroup {
    return new FormGroup({
      [ctrlContainerType]: new FormControl<any>({ value: ContainerTypeEnum.container }),
      [ctrlContainerSearch]: new FormControl<any>(null),
      [ctrlContainerNumber]: new FormControl<any>(null, [...sharedValidator.containerDigits]),
      [ctrlCassetteCode]: new FormControl<any>(null, [spValRequired()]),
      [ctrlContainerPrefix]: new FormControl<any>(null, [...sharedValidator.containerPrefix]),
      [ctrlSequenceNumber]: new FormControl<any>(null),
    });
  }

  onButtonClick(key: string): void {
    this.buttonClick.emit(key);
  }

  triggerChangeDetection(): void {
    this.unitTypeFormView()?.triggerChangeDetection();
    this.searchContainerFormView()?.triggerChangeDetection();
    this.searchCasetteFormView()?.triggerChangeDetection();
    this.trailerFieldsFormView()?.triggerChangeDetection();
    this.miscellaneousFieldsFormView()?.triggerChangeDetection();
  }

  applyFocus(fieldName: string): void {
    this.searchContainerFormView()?.applyFocus(fieldName);
    this.searchCasetteFormView()?.applyFocus(fieldName);
    this.trailerFieldsFormView()?.applyFocus(fieldName);
    this.miscellaneousFieldsFormView()?.applyFocus(fieldName);
  }

  applyAutofocus(): void {
    this.unitTypeFormView()?.applyAutofocus();
    this.searchContainerFormView()?.applyAutofocus();
    this.searchCasetteFormView()?.applyAutofocus();
    this.trailerFieldsFormView()?.applyAutofocus();
    this.miscellaneousFieldsFormView()?.applyAutofocus();
  }

  onContainerTypeChange(event: SharedSearchTypeChangeEvent): void {
    this.setContainerType(event.containerType, event.isOnInitialization);
  }

  onContainerClearRequest(): void {
    if (this.isResetting) {
      return;
    }

    this.containerClear.emit();
    this.clearContainer();
  }

  resetSearch(): void {
    this.isResetting = true;

    const nextType = this.resolveContainerType();

    this.resetSearchFields(nextType);
    this.resetLoadedSeqNr();
    this.resetStoreState();

    setTimeout(() => {
      this.isResetting = false;
      this.applyAutofocus();
    }, 0);
  }

  private ensureContainerTypeControl(): void {
    if (this.formGroup.get(ctrlContainerType)) {
      return;
    }

    this.formGroup.addControl(
      ctrlContainerType,
      new FormControl<any>({ value: ContainerTypeEnum.container })
    );
  }

  private setContainerType(containerType: ContainerTypeEnum, isOnInitalization = false): void {
    if (!isOnInitalization) {
      this.clearContainer();
    }

    this.syncContainerType(containerType);
    this.ctrlContainerNumber().setValue(null, { emitEvent: false });
    this.ctrlContainerPrefix().setValue(
      containerType === ContainerTypeEnum.trailer ? { value: 'T' }
        : containerType === ContainerTypeEnum.miscellaneous ? { value: 'M' }
        : null,
      { emitEvent: false }
    );
  }

  private listenToSeqNr(): void {
    const seqNrSignal = toSignal(
      this.ctrlSeqNr().valueChanges.pipe(
        startWith(this.ctrlSeqNr().value),
        distinctUntilChanged((a, b) => valuePairToValue(a) === valuePairToValue(b))
      ),
      { initialValue: this.ctrlSeqNr().value, injector: this.injector }
    );

    effect(() => {
      const valuePair = seqNrSignal();
      const seqNr = valuePairToValue(valuePair);

      if (this.isResetting) {
        return;
      }

      if (!seqNr) {
        this.resetLoadedSeqNr();
        return;
      }

      if (this.lastLoadedSeqNr === seqNr) {
        return;
      }

      this.lastLoadedSeqNr = seqNr;
      this.appStore.dispatch(sharedContainerSingleActions.load({
        pageType: this.resolvedPageType(),
        data: {
          seqNr,
        }
      }));
    }, { injector: this.injector });
  }

  private clearContainer(): void {
    this.ctrlContainerPrefix().setValue({ value: null }, { emitEvent: false });
    this.ctrlSeqNr().setValue({ value: null }, { emitEvent: false });
    this.resetLoadedSeqNr();
    this.resetStoreState();
  }

  private resolveContainerType(): ContainerTypeEnum {
    return valuePairToValue(this.ctrlContainerType().value) as ContainerTypeEnum
      ?? ContainerTypeEnum.container;
  }

  private resetSearchFields(containerType: ContainerTypeEnum): void {
    this.ctrlContainerSearch().setValue(null, { emitEvent: false });
    this.ctrlContainerNumber().setValue(null, { emitEvent: false });
    this.ctrlCassetteCode().setValue(null, { emitEvent: false });
    this.ctrlContainerPrefix().setValue(
      containerType === ContainerTypeEnum.trailer ? { value: 'T' }
        : containerType === ContainerTypeEnum.miscellaneous ? { value: 'M' }
        : null,
      { emitEvent: false }
    );
    this.ctrlSeqNr().setValue({ value: null }, { emitEvent: false });
    this.syncContainerType(containerType);
  }

  private syncContainerType(containerType: ContainerTypeEnum): void {
    if (valuePairToValue(this.ctrlContainerType().value) !== containerType) {
      this.ctrlContainerType().setValue({ value: containerType }, { emitEvent: false });
    }

    this.containerType.set(containerType);
  }

  private resetLoadedSeqNr(): void {
    this.lastLoadedSeqNr = null;
  }

  private resetStoreState(): void {
    this.appStore.dispatch(sharedContainerSearchActions.reset({
      pageType: this.resolvedPageType(),
    }));
    this.appStore.dispatch(sharedContainerSingleActions.reset({
      pageType: this.resolvedPageType(),
    }));
  }
}
